<template>
  <b-modal
    id="create-award-modal"
    ref="modalRef"
    size="lg"
    centered
    hide-footer
    title="Create an award"
    body-class="p-0"
  >
    <div class="px-5 py-4 award-modal-content">
      <h5 class="mb-4"><strong>Select the award you want to create</strong></h5>

      <b-row>
        <b-col cols="12" md="4" class="mb-3 mb-md-0">
          <div
            class="award-card"
            :class="{ selected: selectedType === RECOGNITION_AWARD_TYPES.PROGRAM }"
            role="button"
            @click="select(RECOGNITION_AWARD_TYPES.PROGRAM)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fad', 'gift']" />
            </div>
            <div class="title">Program award</div>
            <div class="description">
              Create nomination-based awards to promote and recognize specific behaviors or values.
            </div>
          </div>
        </b-col>

        <b-col cols="12" md="4" class="mb-3 mb-md-0">
          <div
            class="award-card"
            :class="{ selected: selectedType === RECOGNITION_AWARD_TYPES.OPEN }"
            role="button"
            @click="select(RECOGNITION_AWARD_TYPES.OPEN)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fad', 'party-horn']" />
            </div>
            <div class="title">Open award</div>
            <div class="description">
              Automatically celebrate key employee moments such as anniversaries or birthdays.
            </div>
          </div>
        </b-col>

        <b-col cols="12" md="4">
          <div
            class="award-card"
            :class="{ selected: selectedType === RECOGNITION_AWARD_TYPES.MILESTONE }"
            role="button"
            @click="select(RECOGNITION_AWARD_TYPES.MILESTONE)"
          >
            <div class="select-indicator"></div>
            <div class="icon">
              <font-awesome-icon :icon="['fad', 'award']" />
            </div>
            <div class="title">Milestone Recognition</div>
            <div class="description">Automate employee key moments, anniversaries, etc</div>
          </div>
        </b-col>
      </b-row>
    </div>

    <div class="px-4 py-3 footer-container">
      <small class="text-muted">
        Not sure? Start with an Open award program. You can add Program and Milestone awards later.
      </small>
      <div class="d-flex flex-nowrap">
        <b-button variant="outline-primary" class="mr-2" @click="onCancel">Cancel</b-button>
        <b-button variant="primary" :disabled="!selectedType" @click="onSubmit"> Create award </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { RECOGNITION_AWARD_TYPES } from '@/pages/recognition/constants';

const selectedType = ref(null);
const modalRef = ref(null);
const $router = useRouter();

function select(type) {
  selectedType.value = type;
}

function onCancel() {
  selectedType.value = null;
  if (modalRef.value) {
    modalRef.value.hide();
  }
}

function onSubmit() {
  if (selectedType.value === RECOGNITION_AWARD_TYPES.PROGRAM) {
    $router.push({ name: 'recognition-create-program' });
  }
  if (selectedType.value === RECOGNITION_AWARD_TYPES.OPEN) {
    $router.push({ name: 'recognition-create-open' });
  }
  if (selectedType.value === RECOGNITION_AWARD_TYPES.MILESTONE) {
    $router.push({ name: 'recognition-create-milestone' });
  }

  if (modalRef.value) {
    modalRef.value.hide();
  }
}
</script>

<style scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.award-modal-content {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.award-card {
  position: relative;
  height: 100%;
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.award-card:hover {
  border-color: var(--palette-color-core-1-lighten-30);
  box-shadow: 0 0 0 2px var(--palette-color-core-1-lighten-50);
}

.award-card.selected {
  border-color: var(--palette-color-core-1-lighten-30);
  box-shadow: 0 0 0 2px var(--palette-color-core-1-lighten-50);
}

.select-indicator {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 2px solid var(--palette-color-default-lighten-90);
  background: var(--palette-color-base-white);
}

.award-card.selected .select-indicator {
  border-color: var(--palette-color-brand-primary);
  background: var(--palette-color-brand-primary);
  box-shadow: inset 0 0 0 4px var(--palette-color-base-white);
}

.icon {
  color: var(--palette-color-brand-primary);
  font-size: 2rem;
  margin-bottom: 0.75rem;
}

.title {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  margin-bottom: 0.5rem;
}

.description {
  color: var(--palette-color-default-lighten-20);
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-bottom: 0.75rem;
}

.footer-container {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12rem;
}

/* Keep button labels on a single line and prevent flex shrink */
.footer-container :deep(.btn) {
  white-space: nowrap;
  min-width: max-content;
  flex: 0 0 auto;
}
</style>
